import { TextFormatTransformer, TextMatchTransformer } from '@lexical/markdown';
import { $isTextNode } from 'lexical';

/**
 * Stores underline as `++text++`, the markdown-it-ins convention. CommonMark
 * and GFM have no underline syntax, and other readers show the plus signs as
 * literal text.
 *
 * `intraword: false` makes the tag match only at a word boundary, so
 * hand-written or agent-written text such as `C++ and C++` stays plain text.
 * Because of this, underline on part of a word does not round-trip.
 */
export const underlineTransformer: TextFormatTransformer = {
  format: ['underline'],
  intraword: false,
  tag: '++',
  type: 'text-format',
};

// Each `+` in a run of two or more. One `+`, as in `a + b`, cannot form a tag.
const PLUS_RUN_CHARACTER = /\+(?=\+)|(?<=\+)\+/g;

// Numeric character reference for `+`. Lexical's Markdown import and
// CommonMark readers decode it back to `+`.
const ESCAPED_PLUS = '&#43;';

// A private-use character that holds the place of an escaped `+` while
// Lexical formats the text. Newer Lexical versions escape `&#` in text, so
// the reference is inserted only after formatting.
const ESCAPED_PLUS_PLACEHOLDER = '\uE000';
const ESCAPED_PLUS_PLACEHOLDER_GLOBAL = new RegExp(
  ESCAPED_PLUS_PLACEHOLDER,
  'g'
);

/**
 * Escapes literal `++` in text so that Markdown import does not read it as
 * an underline tag. Lexical escapes only its built-in format characters and
 * does not remove a backslash before `+` on import, so this transformer
 * writes each `+` of a run as `&#43;`.
 *
 * It must come before other text-match transformers, because Lexical uses
 * the first transformer that exports a node. It never matches on import.
 */
export const literalPlusEscapeTransformer: TextMatchTransformer = {
  dependencies: [],
  export: (node, _exportChildren, exportFormat) => {
    if (!$isTextNode(node) || node.hasFormat('code')) return null;
    const text = node.getTextContent();
    if (!text.includes('++')) return null;
    return exportFormat(
      node,
      text.replace(PLUS_RUN_CHARACTER, ESCAPED_PLUS_PLACEHOLDER)
    ).replace(ESCAPED_PLUS_PLACEHOLDER_GLOBAL, ESCAPED_PLUS);
  },
  // A pattern that never matches: this transformer only affects export.
  importRegExp: /(?!)/,
  regExp: /(?!)/,
  replace: () => {},
  type: 'text-match',
};
